"use client";

import React, { useCallback, useState } from "react";

import { Badge } from "@/components/atoms/Badge/Badge";
import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import { ListingUseType } from "~/types/listing.types";

import {
  VISIBLE_COUNT,
  CATEGORY_GROUP_MAP,
  CATEGORY_GROUP_MAP_BY_USE_TYPE,
  CATEGORY_GROUP_ORDER,
} from "./const/RecommendedVendors.const";
import { RecommendedVendor } from "./RecommendedVendors.types";
import { VendorList } from "./VendorList";

function getGroupedCategory(
  categoryId: string,
  useType?: ListingUseType,
): string {
  const override = useType
    ? CATEGORY_GROUP_MAP_BY_USE_TYPE[useType]?.[categoryId]
    : undefined;
  return override ?? CATEGORY_GROUP_MAP[categoryId] ?? categoryId;
}

type RecommendedVendorsProps = {
  vendors: RecommendedVendor[];
  listingId: string;
  useType?: ListingUseType;
};

export const RecommendedVendors = ({
  vendors,
  listingId,
  useType,
}: RecommendedVendorsProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const categories = Array.from(
    new Set(vendors.map((v) => getGroupedCategory(v.category_id, useType))),
  );

  const filteredVendors =
    selectedCategory === null
      ? vendors
      : vendors.filter(
          (v) =>
            getGroupedCategory(v.category_id, useType) === selectedCategory,
        );

  const orderedCategories = CATEGORY_GROUP_ORDER.filter((group) =>
    categories.includes(group),
  );

  const visibleVendors = isExpanded
    ? filteredVendors
    : filteredVendors.slice(0, VISIBLE_COUNT);
  const hasMore = filteredVendors.length > VISIBLE_COUNT;

  const handleSelectCategory = useCallback((category: string | null) => {
    setSelectedCategory(category);
    setIsExpanded(false);
  }, []);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <div className="tw-mb-40" id="vendors">
      <hr className="tw-border-gray-300 tw-h-[1px] tw-block tw-w-full tw-text-gray-200 tw-mb-40" />
      <Text
        typeface="headline-2-m"
        element="h5"
        className="tw-mb-4 tw-flex tw-items-center"
      >
        Trusted vendors in Chicago
        <Badge appearance="purple" size="xs">
          <Text typeface="caption-1-b" element="p" className="tw-mt-2">
            NEW!
          </Text>
        </Badge>
      </Text>
      <Text typeface="body-1" className="tw-mb-20 tw-text-gray-600">
        Complete your event with handpicked, trusted local vendors.
      </Text>
      <div className="tw-flex tw-flex-nowrap tw-gap-8 tw-mb-16 tw-overflow-x-auto [&::-webkit-scrollbar]:tw-hidden [scrollbar-width:none]">
        <Button
          shape="pill"
          onClick={() => handleSelectCategory(null)}
          className={selectedCategory === null ? "tw-border-base-black" : ""}
        >
          View all
        </Button>
        {orderedCategories.map((category) => (
          <Button
            key={category}
            shape="pill"
            className={
              selectedCategory === category ? "tw-border-base-black" : ""
            }
            onClick={() => handleSelectCategory(category)}
          >
            {category}
          </Button>
        ))}
      </div>
      <VendorList
        isExpanded={isExpanded}
        hasMore={hasMore}
        listingId={listingId}
        vendors={visibleVendors}
        onToggleExpanded={handleToggleExpanded}
      />
    </div>
  );
};
